import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import { Button, Container } from "@mui/material";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Link, Navigate, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import { api } from "../../api/client";
import useClientContext from "../../contexts/useClientContext";
import useClientPageTitle from "../../contexts/useClientPageTitle";
import { pageRoutes } from "../../routes";
import { impersonationSelector } from "../../store/state/user/selectors";
import NewsDetailsArticleContent from "./NewsDetailsArticleContent";
import NewsDetailsVideoContent from "./NewsDetailsVideoContent";

const NewsDetailsPage = () => {
  useClientPageTitle("News");

  const { id } = useParams();
  const { clientCode } = useClientContext();
  const isImpersonation = useSelector(impersonationSelector);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [pageContent, setPageContent] = useState<HTMLDivElement | null>(null);

  const getNewsItem = useCallback(
    (signal?: AbortSignal) => api.investorCommunications.getInvestorCommunication(defined(id), signal),
    [id]
  );

  const [newsItem, fetchError] = useFetch(getNewsItem, { disabled: !id });

  if (!id) {
    return <Navigate to="../" />;
  }

  if (fetchError) {
    logError(fetchError, "[NewsDetailsPage] getInvestorCommunication");
    return <DataLoadingFailed title="Failed to load news" />;
  }

  if (newsItem === undefined) {
    return <InlineLoader />;
  }

  const documentViewSessionId = newsItem.id === id ? newsItem.documentViewSessionId : undefined;

  return (
    <ScrollableFlexContainer containerRef={setScrollContainer}>
      <Container
        ref={setPageContent}
        maxWidth="lg"
        sx={{
          pt: { xs: 2, sm: 4 },
          pb: isImpersonation ? 6 : 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Button
          variant="text"
          color="primary"
          component={Link}
          to={`/${clientCode}/${pageRoutes.news}`}
          endIcon={<ArrowForwardIcon />}
          sx={{ mb: 2.5, alignSelf: "flex-end" }}
        >
          View all news
        </Button>
        {newsItem.type === "Video" ? (
          <NewsDetailsVideoContent
            key={newsItem.id}
            newsItem={newsItem}
            documentViewSessionId={documentViewSessionId}
          />
        ) : (
          <NewsDetailsArticleContent
            key={newsItem.id}
            newsItem={newsItem}
            documentViewSessionId={documentViewSessionId}
            scrollContainer={scrollContainer}
            pageContent={pageContent}
          />
        )}
      </Container>
    </ScrollableFlexContainer>
  );
};

export default NewsDetailsPage;
